import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, MapPin, User, Banknote, AlertCircle, Edit } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { orderAPI, paymentAPI } from '../utils/api';
import { getProductImageSrc } from '../utils/images';
import toast from 'react-hot-toast';

// Declare Layer global type for TypeScript
declare global {
  interface Window {
    Layer: {
      checkout: (config: any, successCallback: any, errorCallback: any) => void;
    };
  }
}

export const Checkout = () => {
  const { items, total, clearCart } = useCartStore();
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    paymentMethod: 'online',
    paymentGateway: 'payglocal', // 'payglocal', 'layer', or 'airpay'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load user profile data if authenticated
  // This will update whenever user data changes (e.g., after profile update)
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        address: user.address?.addressLine1 || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        zipCode: user.address?.postalCode || '',
      }));
    }
  }, [
    isAuthenticated, 
    user?.id,
    user?.email,
    user?.firstName, 
    user?.lastName, 
    user?.phone,
    // Watch address changes by serializing the address object
    user?.address ? JSON.stringify(user.address) : null
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate user authentication
    if (!user?.id) {
      toast.error('User not found. Please login again.');
      return;
    }
    
    // Validate minimum order amount for COD
    if (formData.paymentMethod === 'cod' && total < 199) {
      toast.error('Minimum order amount for COD is ₹199. Please add more items to your cart or choose a different payment method.');
      return;
    }
    
    // Calculate final total including COD charges if applicable
    const finalTotal = (total * 1.18) + (formData.paymentMethod === 'cod' && total < 299 ? 50 : 0);
    
    setIsSubmitting(true);
    
    try {
      // Prepare order items
      const orderItems = items.map(item => ({
        productId: item.product.id,
        productName: item.product.name,
        productImage: item.product.image,
        quantity: item.quantity,
        size: item.size,
        price: item.price,
      }));

      // Prepare shipping address
      const shippingAddress = {
        type: 'shipping',
        street: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: 'India'
      };

      // Create order data for backend
      const orderData = {
        items: orderItems,
        shippingAddress,
        paymentMethod: formData.paymentMethod,
        notes: `Order placed by ${formData.firstName} ${formData.lastName}`,
      };

      // Create order in backend
      console.log('Creating order with data:', orderData);
      const orderResponse = await orderAPI.createOrder(orderData);
      console.log('Order creation response:', orderResponse);
      
      const orderId = orderResponse.data.orderId;
      
      // For COD, just complete the order
      if (formData.paymentMethod === 'cod') {
        clearCart();
        toast.success(`COD Order placed successfully! Order ID: ${orderId}. You will pay ₹${finalTotal.toFixed(2)} when your order is delivered.`);
        navigate('/orders');
        return;
      }
      
      // For online payment, route to selected payment gateway
      if (formData.paymentGateway === 'layer') {
        await initiateLayerPayment(orderId, finalTotal);
      } else if (formData.paymentGateway === 'airpay') {
        await initiateAirpayPayment(orderId);
      } else {
        // PayGlocal payment
        console.log('Initiating PayGlocal payment...');
        const paymentData = {
          orderId: orderId,
          amount: finalTotal,
          customerDetails: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            address: shippingAddress,
          },
          orderItems: orderItems,
        };

        const paymentResponse = await paymentAPI.initiatePayment(paymentData);
        console.log('Payment initiation response:', paymentResponse);

        if (paymentResponse.success && paymentResponse.data.checkoutUrl) {
          // Clear cart before redirecting to payment
          clearCart();
          
          // Show loading message
          toast.loading('Redirecting to payment gateway...', { duration: 2000 });
          
          // Store order ID in session for callback
          sessionStorage.setItem('pendingOrderId', orderId);
          
          // Redirect to PayGlocal checkout
          setTimeout(() => {
            window.location.href = paymentResponse.data.checkoutUrl;
          }, 1000);
        } else {
          throw new Error('Failed to get payment checkout URL');
        }
      }
      
    } catch (error: any) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.message || 'Failed to process order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const initiateAirpayPayment = async (orderId: string) => {
    try {
      console.log('Initiating AirPay payment for order:', orderId);
      
      // Clear cart before redirecting to payment
      clearCart();
      
      // Show loading message
      toast.loading('Redirecting to AirPay payment gateway...', { duration: 2000 });
      
      // Store order ID in session for callback
      sessionStorage.setItem('pendingOrderId', orderId);
      
      // The backend returns HTML directly, so we need to fetch it as text
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 
        (import.meta.env.PROD ? 'https://api.nineeyeshop.com/api' : 'https://api.nineeyeshop.com/api');
      const token = useAuthStore.getState().token;
      
      const htmlResponse = await fetch(`${apiBaseUrl}/payment/airpay/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ orderId })
      });
      
      if (!htmlResponse.ok) {
        const errorData = await htmlResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to initiate AirPay payment');
      }
      
      const htmlContent = await htmlResponse.text();
      
      // Replace page content with AirPay form
      document.open();
      document.write(htmlContent);
      document.close();
    } catch (error: any) {
      console.error('Error initiating AirPay payment:', error);
      toast.error(error.message || 'Failed to initiate AirPay payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const initiateLayerPayment = async (orderId: string, amount: number) => {
    try {
      // Validate phone number for Layer payment
      if (!formData.phone) {
        toast.error('Phone number is required for Layer payment. Please enter your phone number.');
        setIsSubmitting(false);
        return;
      }

      // Create Layer payment token
      const tokenData = {
        amount: amount.toFixed(2),
        contact_number: formData.phone,
        email_id: formData.email,
        currency: 'INR',
        mtx: orderId, // Use order ID as merchant transaction reference
        name: `${formData.firstName} ${formData.lastName}`.trim()
      };

      console.log('Creating Layer payment token:', tokenData);
      const tokenResponse = await paymentAPI.createLayerPaymentToken(tokenData);
      console.log('Layer payment token response:', tokenResponse);

      if (!tokenResponse.success || !tokenResponse.id) {
        throw new Error('Failed to create Layer payment token');
      }

      // Load Layer script if not already loaded
      const layerScriptUrl = tokenResponse.layerScriptUrl || 'https://sandbox-payments.open.money/layer';
      const scriptId = 'layer-checkout-script';

      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = layerScriptUrl;
        script.async = true;
        document.head.appendChild(script);

        // Wait for script to load
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Layer script load timeout')), 10000);
        });
      }

      // Wait for Layer object to be available
      let retries = 0;
      while (typeof window.Layer === 'undefined' && retries < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (typeof window.Layer === 'undefined') {
        throw new Error('Layer payment gateway not loaded. Please try again.');
      }

      // Initialize Layer checkout
      window.Layer.checkout(
        {
          token: tokenResponse.id,
          accesskey: tokenResponse.access_key,
        },
        async (response: any) => {
          console.log('Layer payment response:', response);

          if (response && response.payment_id) {
            // Payment successful
            const responseData = {
              layer_pay_token_id: tokenResponse.id,
              tranid: orderId,
              layer_order_amount: amount.toFixed(2),
              layer_payment_id: response.payment_id,
              hash: tokenResponse.hash
            };

            // Send to backend for verification
            try {
              const verifyResponse = await paymentAPI.handleLayerResponse(responseData);
              console.log('Layer payment verification response:', verifyResponse);

              if (verifyResponse.success && verifyResponse.verified) {
                // Payment successful
                clearCart();
                toast.success(`Payment successful! Order ID: ${orderId}`);
                navigate('/payment/layer/success', { 
                  state: { 
                    orderId,
                    paymentDetails: verifyResponse.payment 
                  } 
                });
              } else {
                // Payment verification failed
                toast.error('Payment verification failed. Please contact support.');
                navigate('/payment/layer/failure', { 
                  state: { 
                    orderId,
                    error: 'Payment verification failed' 
                  } 
                });
              }
            } catch (error: any) {
              console.error('Layer payment verification error:', error);
              toast.error('Payment verification failed. Please contact support if amount was deducted.');
              navigate('/payment/layer/failure', { 
                state: { 
                  orderId,
                  error: error.message 
                } 
              });
            }
          } else {
            // Payment failed or cancelled
            toast.error('Payment failed or cancelled.');
            navigate('/payment/layer/failure', { 
              state: { 
                orderId,
                error: 'Payment failed or cancelled' 
              } 
            });
          }
          setIsSubmitting(false);
        },
        (err: any) => {
          console.error('Layer payment error:', err);
          toast.error(err.message || 'Payment failed. Please try again.');
          setIsSubmitting(false);
        }
      );

    } catch (error: any) {
      console.error('Error initiating Layer payment:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to initiate payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Show empty cart message if no items
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add some items to your cart before checkout</p>
          <Link to="/shop" className="btn-primary btn-lg">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  // Show login prompt for non-authenticated users
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-8">
            Please login to proceed with checkout. Your profile information will be used for shipping.
          </p>
          <div className="space-y-4">
            <Link to="/login" className="btn-primary btn-lg w-full">
              Login to Continue
            </Link>
            <Link to="/shop" className="btn-outline btn-lg w-full">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-8">
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/cart" className="text-primary-600 hover:text-primary-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Contact Information
                </h2>
                {isAuthenticated && (
                  <Link
                    to="/profile"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Profile</span>
                  </Link>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                    disabled={isAuthenticated}
                  />
                  {isAuthenticated && (
                    <p className="text-xs text-gray-500 mt-1">Email from your profile</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                      disabled={isAuthenticated}
                    />
                    {isAuthenticated && (
                      <p className="text-xs text-gray-500 mt-1">From your profile</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                      disabled={isAuthenticated}
                    />
                    {isAuthenticated && (
                      <p className="text-xs text-gray-500 mt-1">From your profile</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                    disabled={isAuthenticated}
                  />
                  {isAuthenticated && (
                    <p className="text-xs text-gray-500 mt-1">From your profile</p>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  Shipping Address
                </h2>
                {isAuthenticated && (
                  <Link
                    to="/profile"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Profile</span>
                  </Link>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                    disabled={isAuthenticated}
                  />
                  {isAuthenticated && (
                    <p className="text-xs text-gray-500 mt-1">From your profile</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                      disabled={isAuthenticated}
                    />
                    {isAuthenticated && (
                      <p className="text-xs text-gray-500 mt-1">From your profile</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                      disabled={isAuthenticated}
                    />
                    {isAuthenticated && (
                      <p className="text-xs text-gray-500 mt-1">From your profile</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className={`input w-full ${isAuthenticated ? 'bg-gray-50' : ''}`}
                    disabled={isAuthenticated}
                  />
                  {isAuthenticated && (
                    <p className="text-xs text-gray-500 mt-1">From your profile</p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                {formData.paymentMethod === 'cod' ? (
                  <Banknote className="h-5 w-5 mr-2 text-green-600" />
                ) : (
                  <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
                )}
                Payment Method
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Payment Method
                  </label>
                  
                  {/* Payment Method Options */}
                  <div className="space-y-3">
                    {/* Online Payment Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        formData.paymentMethod === 'online' 
                          ? 'border-primary-600 bg-primary-50' 
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                      onClick={() => setFormData({...formData, paymentMethod: 'online'})}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="online"
                          checked={formData.paymentMethod === 'online'}
                          onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <CreditCard className="h-5 w-5 text-primary-600" />
                            <h3 className="font-semibold text-gray-900">Online Payment</h3>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Pay securely using Credit/Debit Card, UPI, Net Banking, or Wallets
                          </p>
                          <div className="mt-2 text-xs text-primary-600 font-medium">
                            ✓ Instant confirmation • ✓ Secure checkout • ✓ No additional charges
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cash on Delivery Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        formData.paymentMethod === 'cod' 
                          ? 'border-green-600 bg-green-50' 
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                      onClick={() => setFormData({...formData, paymentMethod: 'cod'})}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cod"
                          checked={formData.paymentMethod === 'cod'}
                          onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Banknote className="h-5 w-5 text-green-600" />
                            <h3 className="font-semibold text-gray-900">Cash on Delivery</h3>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Pay cash when your order is delivered to your doorstep
                          </p>
                          <div className="mt-2 text-xs text-gray-600">
                            • Minimum order: ₹199 • ₹50 COD charges for orders below ₹299
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Gateway Selection */}
                {formData.paymentMethod === 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Payment Gateway
                    </label>
                    <div className="space-y-3">
                      {/* PayGlocal Gateway */}
                      <div 
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                          formData.paymentGateway === 'payglocal' 
                            ? 'border-primary-600 bg-primary-50' 
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        onClick={() => setFormData({...formData, paymentGateway: 'payglocal'})}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="paymentGateway"
                            value="payglocal"
                            checked={formData.paymentGateway === 'payglocal'}
                            onChange={(e) => setFormData({...formData, paymentGateway: e.target.value})}
                            className="mt-0"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">PayGlocal Payment Gateway</h4>
                            <p className="text-xs text-gray-600 mt-0.5">
                              Fast and secure payment processing
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Layer Gateway */}
                      <div 
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                          formData.paymentGateway === 'layer' 
                            ? 'border-primary-600 bg-primary-50' 
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        onClick={() => setFormData({...formData, paymentGateway: 'layer'})}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="paymentGateway"
                            value="layer"
                            checked={formData.paymentGateway === 'layer'}
                            onChange={(e) => setFormData({...formData, paymentGateway: e.target.value})}
                            className="mt-0"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">Open Payment Gateway</h4>
                            <p className="text-xs text-gray-600 mt-0.5">
                              Secure payment gateway with multiple options
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* AirPay Gateway */}
                      <div 
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                          formData.paymentGateway === 'airpay' 
                            ? 'border-primary-600 bg-primary-50' 
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        onClick={() => setFormData({...formData, paymentGateway: 'airpay'})}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="paymentGateway"
                            value="airpay"
                            checked={formData.paymentGateway === 'airpay'}
                            onChange={(e) => setFormData({...formData, paymentGateway: e.target.value})}
                            className="mt-0"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">AirPay Payment Gateway</h4>
                            <p className="text-xs text-gray-600 mt-0.5">
                              UPI, Cards, Net Banking & more
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Online Payment Information */}
                {formData.paymentMethod === 'online' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <CreditCard className="h-5 w-5 text-blue-600 mt-0.5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-blue-800 mb-2">
                          Secure Online Payment
                        </h3>
                        <div className="text-sm text-blue-700 space-y-1">
                          <p>• All major credit/debit cards accepted</p>
                          <p>• UPI, Net Banking, and Wallets supported</p>
                          <p>• 100% secure and encrypted payment</p>
                          <p>• Instant order confirmation</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* COD Information */}
                {formData.paymentMethod === 'cod' && (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <Banknote className="h-5 w-5 text-green-600 mt-0.5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-green-800 mb-2">
                            Cash on Delivery Selected
                          </h3>
                          <div className="text-sm text-green-700 space-y-1">
                            <p>• Pay when your order is delivered</p>
                            <p>• No advance payment required</p>
                            <p>• Cash payment to delivery person</p>
                            <p>• Available for orders above ₹199</p>
                            <p>• ₹50 COD charges for orders below ₹299</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-amber-800 mb-1">
                            Important Notice
                          </h3>
                          <p className="text-sm text-amber-700">
                            Please keep exact change ready for the delivery person. COD orders may take 1-2 extra days for processing.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={`${item.product.id}-${item.size}`} className="flex space-x-3">
                    <img
                      src={getProductImageSrc(item.product.image)}
                      alt={item.product.name}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/placeholder.png';
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{item.product.name}</h4>
                      <p className="text-sm text-gray-500">Size: {item.size}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">₹{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">Free</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">₹{(total * 0.18).toFixed(2)}</span>
                </div>
                {formData.paymentMethod === 'cod' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">COD Charges</span>
                    <span className="font-medium">₹{(total >= 299 ? 0 : 50).toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>₹{((total * 1.18) + (formData.paymentMethod === 'cod' && total < 299 ? 50 : 0)).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* COD Notice in Order Summary */}
              {formData.paymentMethod === 'cod' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Banknote className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Cash on Delivery</span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    Pay ₹{((total * 1.18) + (total < 299 ? 50 : 0)).toFixed(2)} when your order is delivered
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full btn-lg mt-6 ${
                  formData.paymentMethod === 'cod' 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'btn-primary'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    {formData.paymentMethod === 'cod' ? (
                      <>
                        <Banknote className="h-5 w-5" />
                        <span>Place COD Order</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5" />
                        <span>Proceed to Payment</span>
                      </>
                    )}
                  </div>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
